import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Store, Package, Tag, ListOrdered, ClipboardList, PhoneCall, ShoppingCart, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import BrandsSection from "@/components/customer/BrandsSection";
import ProductsSection from "@/components/customer/ProductsSection";
import PriceListSection from "@/components/customer/PriceListSection";
import OrdersSection from "@/components/customer/OrdersSection";
import ContactSection from "@/components/customer/ContactSection";
import CartDrawer from "@/components/customer/CartDrawer";
import ChangePinDialog from "@/components/customer/ChangePinDialog";
import CustomerCare from "@/components/customer/CustomerCare";
import AdminTopNav from "@/components/AdminTopNav";

const NAV = [
  { key: "brands", label: "Our Brands", icon: Store },
  { key: "products", label: "Products", icon: Package },
  { key: "offers", label: "Offers", icon: Tag },
  { key: "pricelist", label: "Price List", icon: ListOrdered },
  { key: "orders", label: "Orders", icon: ClipboardList },
  { key: "contact", label: "Contact Us", icon: PhoneCall },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cartOpen, setCartOpen] = useState(false);
  const [ordersKey, setOrdersKey] = useState(0);

  // URL-driven navigation so the Android system Back button pops through browser history:
  //   Our Brands (/dashboard)  ->  Brand Products (/dashboard?tab=products&brand=X)  -> Back -> Our Brands
  const active = searchParams.get("tab") || "brands";
  const brandFilter = searchParams.get("brand") || null;
  const brandsScrollY = useRef(0);

  // Restore the Our Brands scroll position when the Back button returns us there.
  useEffect(() => {
    if (active === "brands") {
      requestAnimationFrame(() => window.scrollTo(0, brandsScrollY.current));
    }
  }, [active, brandFilter]);

  const goProductsForBrand = (brand) => {
    brandsScrollY.current = window.scrollY;
    // push a new history entry so Android Back returns to Our Brands
    setSearchParams({ tab: "products", brand }, { replace: false });
  };

  const clearBrand = () => {
    if (brandFilter) {
      navigate(-1); // go back to Our Brands via real browser history
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const selectTab = (key) => {
    const next = key === "brands" ? {} : { tab: key };
    // tab switches replace the current entry (only the brand drill-down adds to history)
    setSearchParams(next, { replace: true });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/60 to-white">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-blue-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <img src="/slm-logo.png" alt="SLM" className="h-11 w-auto object-contain" />
            <div className="leading-tight">
              <p className="text-sm font-bold text-blue-800 sm:text-base">Sri Lakshmi Agencies</p>
              <p className="text-[11px] font-medium text-slate-500">Ramakuppam</p>
              <span data-testid="customer-store-label" className="mt-0.5 inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-blue-700 ring-1 ring-blue-100">
                Customer Store
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="open-cart-button"
              onClick={() => setCartOpen(true)}
              className="relative grid h-10 w-10 place-items-center rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              aria-label="Open cart"
            >
              <ShoppingCart className="h-5 w-5" />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -right-1.5 -top-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-amber-500 px-1 text-[11px] font-bold text-white"
                >
                  {count}
                </span>
              )}
            </button>
            {user?.role === "user" && <ChangePinDialog />}
            {user?.role === "admin" ? <div className="hidden sm:block"><AdminTopNav current="store" onLogout={handleLogout} /></div> : (
              <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="grid h-10 w-10 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:text-red-500"
              aria-label="Logout"
            >
              <LogOut className="h-5 w-5" />
            </button>
            )}
          </div>
        </div>
        {user?.role === "admin" && (
          <div className="mx-auto max-w-6xl px-4 pb-2 sm:hidden">
            <AdminTopNav current="store" onLogout={handleLogout} full />
          </div>
        )}

        {user?.role === "user" && (user.shop_name || user.name) && (
          <div className="mx-auto max-w-6xl px-4 pb-2">
            <div data-testid="customer-shop-banner" className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-[#EEF4FB] via-[#F3F0FA] to-[#FBF6EA] px-4 py-2.5 ring-1 ring-indigo-100">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-500 text-white shadow-md shadow-indigo-300/40">
                <Store className="h-5 w-5" />
              </span>
              <div className="min-w-0 leading-tight">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-indigo-400">Welcome</p>
                <p data-testid="customer-shop-name" className="truncate bg-gradient-to-r from-blue-800 to-indigo-600 bg-clip-text text-lg font-extrabold text-transparent sm:text-xl">
                  {user.shop_name || user.name}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <nav className="mx-auto max-w-6xl overflow-x-auto px-2 pb-2">
          <div className="flex gap-1">
            {NAV.map((n) => {
              const Icon = n.icon;
              const isActive = active === n.key;
              return (
                <button
                  key={n.key}
                  data-testid={`nav-${n.key}`}
                  onClick={() => selectTab(n.key)}
                  className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "bg-blue-600 text-white"
                      : "text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {n.label}
                </button>
              );
            })}
          </div>
        </nav>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-6xl px-4 pb-24 pt-6">
        {active === "brands" && <BrandsSection onSelectBrand={goProductsForBrand} />}
        {active === "products" && (
          <ProductsSection
            mode="products"
            brandFilter={brandFilter}
            onClearBrand={clearBrand}
          />
        )}
        {active === "offers" && <ProductsSection mode="offers" />}
        {active === "pricelist" && <PriceListSection />}
        {active === "orders" && <OrdersSection key={ordersKey} />}
        {active === "contact" && <ContactSection />}
      </main>

      {!cartOpen && <CustomerCare />}

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        onOrderPlaced={() => setOrdersKey((k) => k + 1)}
      />
    </div>
  );
}
